import React, { useMemo } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { DROPDOWN_DEFAULT_TEXT } from '../../utility/constants'

const FILTER_WIDTH = 150

function ActivityFilter({
  tasks,
  categories,
  filter,
  applyFilter,
  filterCategories,
  isCardView,
  isActiveTasks,
}) {
  const filterCustom =
    isCardView && isActiveTasks && filterCategories?.length > 0 ? filterCategories : tasks

  const categoryNameMap = useMemo(() => {
    const map = new Map()

    categories?.forEach((cat) => {
      map.set(cat.id, cat.name)
    })

    return map
  }, [categories])

  const categoryOptions = useMemo(() => {
    const categoryMap = new Map()

    filterCustom?.forEach((task) => {
      const categoryName = categoryNameMap.get(task.process_category_id)

      if (categoryName && !categoryMap.has(task.process_category_id)) {
        categoryMap.set(task.process_category_id, {
          id: task.process_category_id,
          name: categoryName,
        })
      }
    })

    return [...categoryMap.values()]
  }, [filterCustom, categoryNameMap])

  const processOptions = useMemo(() => {
    if (!filter.categoryId) return []

    const processMap = new Map()

    filterCustom?.forEach((task) => {
      if (String(task.process_category_id) === String(filter.categoryId)) {
        const templateName = task.process_template_name || task.process_templates

        if (templateName && !processMap.has(task.process_template_id)) {
          processMap.set(task.process_template_id, {
            id: task.process_template_id,
            name: templateName,
          })
        }
      }
    })

    return [...processMap.values()]
  }, [filter.categoryId, filterCustom])

  const activityOptions = useMemo(() => {
    if (!filter.processTemplateId) return []

    const activityMap = new Map()

    filterCustom?.forEach((task) => {
      if (task.process_template_id === filter.processTemplateId) {
        if (task.activity_template_name && !activityMap.has(task.activity_template_id)) {
          activityMap.set(task.activity_template_id, {
            id: task.activity_template_id,
            name: task.activity_template_name,
          })
        }
      }
    })

    return [...activityMap.values()]
  }, [filter.processTemplateId, filterCustom])

  const isValidOption = (value, options) => options.some((opt) => opt.id === value)

  const getSelectValue = (value, options) =>
    value && isValidOption(value, options) ? value : DROPDOWN_DEFAULT_TEXT

  return (
    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
      <FormControl className="task-filter" size="small" sx={{ width: FILTER_WIDTH }}>
        <InputLabel>Category</InputLabel>

        <Select
          value={getSelectValue(filter.categoryId, categoryOptions)}
          label="Category"
          onChange={(e) => {
            const selected = e.target.value

            applyFilter({
              categoryId: selected !== DROPDOWN_DEFAULT_TEXT ? selected : null,
              processTemplateId: null,
              activityTemplateId: null,
            })
          }}
          style={{ borderRadius: '50px' }}
        >
          <MenuItem value="All">All</MenuItem>

          {categoryOptions.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>
              {cat.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {filter.categoryId && (
        <FormControl className="task-filter" size="small" sx={{ width: FILTER_WIDTH }}>
          <InputLabel>Process</InputLabel>

          <Select
            value={getSelectValue(filter.processTemplateId, processOptions)}
            label="Process"
            onChange={(e) => {
              const selected = e.target.value

              applyFilter({
                ...filter,
                processTemplateId: selected !== DROPDOWN_DEFAULT_TEXT ? selected : null,
                activityTemplateId: null,
              })
            }}
            style={{ borderRadius: '50px' }}
          >
            <MenuItem value="All">All</MenuItem>

            {processOptions.map((proc) => (
              <MenuItem key={proc.id} value={proc.id}>
                {proc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {filter.processTemplateId && (
        <FormControl className="task-filter" size="small" sx={{ width: FILTER_WIDTH }}>
          <InputLabel>Activity</InputLabel>

          <Select
            value={getSelectValue(filter.activityTemplateId, activityOptions)}
            label="Activity"
            onChange={(e) => {
              const selected = e.target.value

              applyFilter({
                ...filter,
                activityTemplateId: selected !== DROPDOWN_DEFAULT_TEXT ? selected : null,
              })
            }}
            style={{ borderRadius: '50px' }}
          >
            <MenuItem value="All">All</MenuItem>

            {activityOptions.map((act) => (
              <MenuItem key={act.id} value={act.id}>
                {act.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  )
}

export default React.memo(ActivityFilter)
